import { useSyncExternalStore } from 'react';
import {
  DEFAULT_ROLE_TITLES,
  DEFAULT_TERMS,
  type RoleTitles,
  type TermKey,
  type Terms,
} from './workspaceTypes';

/**
 * Wording for the workspace that is open. Screens read from here so a
 * membership association sees "Members" and "Secretary" while a service
 * company sees "Customers" and "Admin", without every screen loading settings.
 */
type Labels = { terms: Terms; roles: RoleTitles };

let current: Labels = { terms: DEFAULT_TERMS, roles: DEFAULT_ROLE_TITLES };
const listeners = new Set<() => void>();

export function setWorkspaceLabels(next: Labels): void {
  current = next;
  for (const listener of listeners) listener();
}

export function resetWorkspaceLabels(): void {
  setWorkspaceLabels({ terms: DEFAULT_TERMS, roles: DEFAULT_ROLE_TITLES });
}

export function term(key: TermKey): string {
  return current.terms[key] || DEFAULT_TERMS[key];
}

/** Role title for the open workspace, or null when the role is not one we rename. */
export function workspaceRoleTitle(role: string): string | null {
  return (current.roles as Record<string, string>)[role] ?? null;
}

/** The workspace's own title for a role, or null when it keeps the platform default. */
export function renamedRoleTitle(role: string): string | null {
  const title = (current.roles as Record<string, string>)[role];
  if (!title || title === (DEFAULT_ROLE_TITLES as Record<string, string>)[role]) return null;
  return title;
}

/** "Secretary (Admin)" when renamed, otherwise the fallback label. */
export function roleLabelWithTitle(role: string, fallback: string): string {
  const title = renamedRoleTitle(role);
  return title ? `${title} (${fallback})` : fallback;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-render when the open workspace's wording changes. */
export function useWorkspaceLabels(): Labels {
  return useSyncExternalStore(subscribe, () => current, () => current);
}
